import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Delete, ArrowBack, Add } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import TemplateForm from './TemplateForm';

export type Template = {
  uid_whatsapp_template: string;
  uid_waba_numero: string;
  nome_meta: string;
  categoria: 'marketing' | 'utilidade' | 'autenticacao';
  idioma: string;
  texto_cabecalho?: string | null;
  texto_corpo: string;
  variaveis?: { chave: string; componente: 'cabecalho' | 'corpo'; parameter_name?: string }[] | null;
  botoes?: { texto: string; payload: string }[] | null;
  status_meta: 'em_analise' | 'aprovado' | 'rejeitado';
  ativo: boolean;
};

const CATEGORIA_LABEL: Record<string, string> = {
  marketing: 'Marketing',
  utilidade: 'Utilidade',
  autenticacao: 'Autenticação',
};

// Só templates "aprovado" (e ativos) aparecem pro atendente escolher na
// tela de Mensagens — por isso vale deixar bem visível aqui na listagem.
const STATUS_META_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  em_analise: { label: 'Em análise', color: '#856404', bg: '#fff3cd' },
  aprovado: { label: 'Aprovado', color: '#155724', bg: '#d4edda' },
  rejeitado: { label: 'Rejeitado', color: '#721c24', bg: '#f8d7da' },
};

const TemplatesPage = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [numeroPorUid, setNumeroPorUid] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [templateEditando, setTemplateEditando] = useState<Template | null>(null);
  const [atualizandoAtivoUid, setAtualizandoAtivoUid] = useState<string | null>(null);

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    api.get('/pesca/templates')
      .then(res => setTemplates(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Mostra qual número de WhatsApp está associado a cada template — hoje a
  // listagem não deixava isso visível, o que ajudava a esconder o bug do
  // uid_waba_numero fixo (ver TemplateForm).
  useEffect(() => {
    api.get('/pesca/waba-numeros')
      .then(res => {
        const mapa: Record<string, string> = {};
        for (const n of res.data.data || []) mapa[n.uid_waba_numero] = n.display_phone_number;
        setNumeroPorUid(mapa);
      })
      .catch(() => {});
  }, []);

  const handleNovo = () => {
    setTemplateEditando(null);
    setFormOpen(true);
  };

  const handleEditar = (template: Template) => {
    setTemplateEditando(template);
    setFormOpen(true);
  };

  const handleSalvo = () => {
    setFormOpen(false);
    fetchTemplates();
  };

  const handleExcluir = async (template: Template) => {
    if (!window.confirm(`Remover o template "${template.nome_meta}"? Ele deixa de aparecer em qualquer lugar, mas não é apagado de vez.`)) return;
    try {
      await api.delete(`/pesca/templates/${template.uid_whatsapp_template}`);
      fetchTemplates();
    } catch (error) {
      console.error('Erro ao remover template:', error);
    }
  };

  // Toggle ativo/inativo direto na listagem — sem exclusão física (PUT com ativo: false/true)
  const toggleAtivo = async (template: Template) => {
    setAtualizandoAtivoUid(template.uid_whatsapp_template);
    try {
      await api.put(`/pesca/templates/${template.uid_whatsapp_template}`, { ativo: !template.ativo });
      fetchTemplates();
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
    } finally {
      setAtualizandoAtivoUid(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Templates de WhatsApp</h1>

        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: '10px' }}>
            Qtd: <span style={{ fontWeight: 'bold', color: 'var(--green-700)' }}>{templates.length}</span>
          </p>

          <button className={styles.blueBtn} onClick={handleNovo} title="Adicionar Novo">
            <Add />
          </button>
          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Número</th>
              <th>Categoria</th>
              <th>Idioma</th>
              <th>Aprovação</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => {
              const badge = STATUS_META_BADGE[template.status_meta] || STATUS_META_BADGE.em_analise;
              return (
                <tr key={template.uid_whatsapp_template}>
                  <td>{template.nome_meta}</td>
                  <td>{numeroPorUid[template.uid_waba_numero] || '—'}</td>
                  <td>{CATEGORIA_LABEL[template.categoria] || template.categoria}</td>
                  <td>{template.idioma}</td>
                  <td>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700, color: badge.color,
                      background: badge.bg, padding: '3px 10px', borderRadius: '10px',
                    }}>
                      {badge.label}
                    </span>
                  </td>
                  <td>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={template.ativo}
                        disabled={atualizandoAtivoUid === template.uid_whatsapp_template}
                        onChange={() => toggleAtivo(template)}
                      />
                      {template.ativo ? 'Ativo' : 'Inativo'}
                    </label>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <Edit fontSize="small" style={{ color: '#03c9fe', cursor: 'pointer' }} onClick={() => handleEditar(template)} />
                      <Delete fontSize="small" style={{ color: '#f75a68', cursor: 'pointer' }} onClick={() => handleExcluir(template)} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && templates.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  Nenhum template cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <TemplateForm
          template={templateEditando}
          onClose={() => setFormOpen(false)}
          onSaved={handleSalvo}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
